import React from 'react';
import {
  SCENE_PREFIXES,
  TRANSITIONS,
  CHARACTER_EXTENSIONS,
  normalizeNewlines
} from '../utils/fountainParser';

// Base Letter measurements — industry standard
const BASE = {
  PAGE_WIDTH: 8.5,
  PAGE_HEIGHT: 11,
  LEFT_MARGIN: 1.5,
  RIGHT_MARGIN: 1.0,
  TOP_MARGIN: 1.0,
  BOTTOM_MARGIN: 1.0,
  CHARACTER_INDENT: 2.2,
  PARENTHETICAL_INDENT: 1.6,
  DIALOGUE_INDENT: 1,
  DIALOGUE_WIDTH: 3.5,
  LINE_HEIGHT: 6/72,
  LINES_PER_PAGE: 55
};

function scaledMeasurements(pageWidth, pageHeight) {
  const s = pageWidth / BASE.PAGE_WIDTH;
  return {
    PAGE_WIDTH: pageWidth,
    PAGE_HEIGHT: pageHeight,
    LEFT_MARGIN: +(BASE.LEFT_MARGIN * s).toFixed(3),
    RIGHT_MARGIN: +(BASE.RIGHT_MARGIN * s).toFixed(3),
    TOP_MARGIN: +(BASE.TOP_MARGIN * s).toFixed(3),
    BOTTOM_MARGIN: +(BASE.BOTTOM_MARGIN * s).toFixed(3),
    CHARACTER_INDENT: +(BASE.CHARACTER_INDENT * s).toFixed(3),
    PARENTHETICAL_INDENT: +(BASE.PARENTHETICAL_INDENT * s).toFixed(3),
    DIALOGUE_INDENT: +(BASE.DIALOGUE_INDENT * s).toFixed(3),
    DIALOGUE_WIDTH: +(BASE.DIALOGUE_WIDTH * s).toFixed(3),
    LINE_HEIGHT: BASE.LINE_HEIGHT,
    LINES_PER_PAGE: Math.floor(BASE.LINES_PER_PAGE * (pageHeight / BASE.PAGE_HEIGHT))
  };
}

function elementStyles(meas) {
  const CONTENT_WIDTH = meas.PAGE_WIDTH - meas.LEFT_MARGIN - meas.RIGHT_MARGIN;
  const DIALOGUE_RIGHT_MARGIN = CONTENT_WIDTH - meas.DIALOGUE_INDENT - meas.DIALOGUE_WIDTH;
  const base = {
    fontFamily: 'Courier Prime, Courier, monospace',
    fontSize: '12pt',
    lineHeight: '1',
    whiteSpace: 'pre-wrap',
    margin: 0,
    padding: 0
  };
  return {
    scene: { ...base, fontWeight: 'bold', textTransform: 'uppercase', paddingTop: '6pt', paddingBottom: '6pt' },
    action: { ...base, paddingTop: '6pt', paddingBottom: '6pt' },
    character: { ...base, paddingTop: '6pt', textTransform: 'uppercase', marginLeft: `${meas.CHARACTER_INDENT}in` },
    parenthetical: { ...base, marginLeft: `${meas.PARENTHETICAL_INDENT}in`, marginRight: `${meas.PARENTHETICAL_INDENT}in`, paddingBottom: '6pt' },
    dialogue: { ...base, marginLeft: `${meas.DIALOGUE_INDENT}in`, marginRight: `${DIALOGUE_RIGHT_MARGIN}in`, paddingBottom: '6pt' },
    transition: { ...base, textAlign: 'right', marginRight: 0, textTransform: 'uppercase', paddingTop: '6pt', paddingBottom: '6pt' },
    blank: { ...base, height: '6pt' },
    note: { ...base, color: '#666', fontStyle: 'italic', paddingTop: '6pt', paddingBottom: '6pt' }
  };
}

function formatContentToPages(content, meas) {
  const scriptContent = normalizeNewlines(String(content))
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const lines = scriptContent.split('\n');
  const formatted = [];
  let previousElement = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const upper = trimmed.toUpperCase();
    let elementType;
    let isForced = false;
    let extension = '';

    if (!trimmed) {
      formatted.push({ key: i, type: 'blank', text: '' });
      continue;
    }

    if (trimmed.startsWith('!')) { isForced = true; elementType = 'action'; }
    else if (trimmed.startsWith('.')) { isForced = true; elementType = 'scene'; }
    else if (trimmed.startsWith('@')) { isForced = true; elementType = 'character'; }
    else if (trimmed.startsWith('>') || trimmed.endsWith('<')) { isForced = true; elementType = 'transition'; }
    else if (trimmed.startsWith('~')) { isForced = true; elementType = 'dialogue'; }
    else if (trimmed.startsWith('[[')) { elementType = 'note'; }
    else {
      const isSceneHeader = SCENE_PREFIXES.some(prefix => upper.startsWith(prefix) || upper.startsWith('.' + prefix));
      if (isSceneHeader) elementType = 'scene';
      else if (TRANSITIONS.includes(upper) || (upper.endsWith('TO:') && upper === upper.toUpperCase())) elementType = 'transition';
      else if (trimmed.match(/^[A-Z][A-Z0-9\s\-']*(\(.*\))?$/) && !SCENE_PREFIXES.some(prefix => upper.startsWith(prefix))) {
        const match = trimmed.match(/^([A-Z][A-Z0-9\s\-']*?)(\(.*\))?$/);
        if (match) {
          const name = match[1];
          const ext = match[2];
          if (!name.endsWith('TO') && !TRANSITIONS.some(trans => name.includes(trans.replace(/\s*TO:?$/, '')))) {
            elementType = 'character';
            if (ext) {
              let upperExt = ext.toUpperCase().replace(/[()]/g, '').trim();
              const standard = CHARACTER_EXTENSIONS.find(e => upperExt === e || upperExt === e.replace(/\./g, '') || e.includes(upperExt));
              extension = standard ? `(${standard})` : ext.trim();
            }
          } else {
            elementType = 'transition';
          }
        }
      } else if (trimmed.match(/^\([\s\S]*\)$/)) { elementType = 'parenthetical'; }
      else if (previousElement === 'character' || previousElement === 'parenthetical') { elementType = 'dialogue'; }
      else { elementType = 'action'; }
    }

    let cleaned = trimmed
      .replace(/^[!@>~]/, '')
      .replace(/^\.+/, '')
      .replace(/\[\[([^\]]*)\]\]/, '')
      .replace(/^>|<$/, '')
      .replace(/#([a-zA-Z0-9.\-]+)#\s*$/, '') // Remove scene numbers (#1A#)
      .trim();

    if (elementType === 'character') {
      let characterName = cleaned;
      if (extension) {
        characterName = characterName.replace(/\(.*\)/, '').trim();
      }
      cleaned = characterName.toUpperCase() + (extension ? ' ' + extension : '');
    }
    if (elementType === 'scene' || elementType === 'transition') {
      cleaned = cleaned.toUpperCase();
      if (elementType === 'transition' && !cleaned.endsWith('TO:') && !cleaned.includes('FADE') && !cleaned.includes('CUT') && !cleaned.includes('DISSOLVE')) {
        cleaned += ' TO:';
      }
    }

    formatted.push({ key: i, type: elementType, text: cleaned });
    previousElement = elementType;
  }

  // Pagination (capacity-aware)
  const pages = [];
  let currentPage = [];
  let lineCount = 0;
  let dialogueBlock = null;
  const contentWidth = meas.PAGE_WIDTH - meas.LEFT_MARGIN - meas.RIGHT_MARGIN;

  const calcLines = (ln) => {
    let available = contentWidth;
    if (ln.type === 'dialogue') available = meas.DIALOGUE_WIDTH;
    else if (ln.type === 'parenthetical') available = contentWidth - (meas.PARENTHETICAL_INDENT * 2);
    const charsPerLine = Math.floor(available * 12);
    const base = ['dialogue', 'action', 'parenthetical'].includes(ln.type)
      ? Math.ceil(Math.max(1, ln.text.length) / Math.max(1, charsPerLine))
      : 1;
    return ln.type !== 'blank' ? base + 0.5 : base;
  };

  const isDialogueElement = (t) => ['character', 'parenthetical', 'dialogue'].includes(t);

  const breakPage = () => {
    if (currentPage.length > 0) {
      pages.push([...currentPage]);
      currentPage = [];
      lineCount = 0;
    }
  };

  for (let i = 0; i < formatted.length; i++) {
    const ln = formatted[i];
    const elLines = calcLines(ln);

    // Force each scene to start on a new page
    if (ln.type === 'scene' && (currentPage.length > 0 || lineCount > 0)) {
      breakPage();
    }

    // Accumulate dialogue block
    if (ln.type === 'character') {
      dialogueBlock = { character: ln, elements: [ln], total: elLines };
      continue;
    }
    if (dialogueBlock && isDialogueElement(ln.type)) {
      dialogueBlock.elements.push(ln);
      dialogueBlock.total += elLines;
      continue;
    }

    // Close dialogue block before processing non-dialogue element
    if (dialogueBlock && !isDialogueElement(ln.type)) {
      const remaining = meas.LINES_PER_PAGE - lineCount;
      // Scene heading protection for the dialogue block start
      if (dialogueBlock.character && lineCount > 0 && remaining < 3) {
        breakPage();
      }
      // Capacity check for the whole dialogue block
      if (dialogueBlock.total > (meas.LINES_PER_PAGE - lineCount) && lineCount > 0) {
        // Split dialogue block with (MORE) / (CONT'D)
        const charLine = dialogueBlock.character;
        let placed = [charLine];
        let placedLines = calcLines(charLine);
        for (let di = 1; di < dialogueBlock.elements.length; di++) {
          const dEl = dialogueBlock.elements[di];
          const dLines = calcLines(dEl);
          if (lineCount + placedLines + dLines + 1 > meas.LINES_PER_PAGE) break;
          placed.push(dEl);
          placedLines += dLines;
        }
        // Add (MORE)
        placed.push({ key: `more-${charLine.key}`, type: 'character', text: '(MORE)' });
        currentPage.push(...placed);
        lineCount += placedLines + 1;
        breakPage();
        // Add CHARACTER (CONT'D) + remaining
        const charName = charLine.text.replace(/\s*\([^)]*\)$/, '').trim();
        const ext = charLine.extension || '';
        const contd = ext ? `${charName} ${ext} (CONT'D)` : `${charName} (CONT'D)`;
        currentPage.push({ key: `contd-${charLine.key}`, type: 'character', text: contd });
        lineCount += 1;
        const placedCount = placed.length - 2; // minus character + MORE
        for (let di = 1 + placedCount; di < dialogueBlock.elements.length; di++) {
          currentPage.push(dialogueBlock.elements[di]);
          lineCount += calcLines(dialogueBlock.elements[di]);
        }
      } else {
        currentPage.push(...dialogueBlock.elements);
        lineCount += dialogueBlock.total;
      }
      dialogueBlock = null;
      // Continue to process current ln below
    }

    // Capacity-aware placement for the current element
    let remaining = meas.LINES_PER_PAGE - lineCount;
    // Keep scene headings (sluglines) attached to at least one following line
    if (ln.type === 'scene') {
      const minFollow = 3; // reserve three lines after the heading
      if ((elLines + minFollow) > remaining) {
        breakPage();
        remaining = meas.LINES_PER_PAGE - lineCount;
      }
    }
    // If element won't fit, break
    if (elLines > remaining) {
      breakPage();
    }
    currentPage.push(ln);
    lineCount += elLines;
  }
  if (dialogueBlock) {
    // Place remaining dialogue block with capacity check
    if (dialogueBlock.total > (meas.LINES_PER_PAGE - lineCount) && lineCount > 0) {
      // Split with (MORE)/(CONT'D)
      const charLine = dialogueBlock.character;
      let placed = [charLine];
      let placedLines = calcLines(charLine);
      for (let di = 1; di < dialogueBlock.elements.length; di++) {
        const dEl = dialogueBlock.elements[di];
        const dLines = calcLines(dEl);
        if (lineCount + placedLines + dLines + 1 > meas.LINES_PER_PAGE) break;
        placed.push(dEl);
        placedLines += dLines;
      }
      placed.push({ key: `more-${charLine.key}`, type: 'character', text: '(MORE)' });
      currentPage.push(...placed);
      lineCount += placedLines + 1;
      breakPage();
      const charName = charLine.text.replace(/\s*\([^)]*\)$/, '').trim();
      const ext = charLine.extension || '';
      const contd = ext ? `${charName} ${ext} (CONT'D)` : `${charName} (CONT'D)`;
      currentPage.push({ key: `contd-${charLine.key}`, type: 'character', text: contd });
      lineCount += 1;
      const placedCount = placed.length - 2;
      for (let di = 1 + placedCount; di < dialogueBlock.elements.length; di++) {
        currentPage.push(dialogueBlock.elements[di]);
        lineCount += calcLines(dialogueBlock.elements[di]);
      }
    } else {
      currentPage.push(...dialogueBlock.elements);
    }
  }
  if (currentPage.length > 0) pages.push(currentPage);

  return pages;
}

function buildSceneHeader(scene) {
  const type = (scene.location_type || '').toUpperCase().replace('INT/EXT', 'INT/EXT.').replace('I/E', 'I/E.');
  const loc = (scene.location || '').trim();
  const area = scene.area ? ` - ${scene.area.trim()}` : '';
  const time = (scene.time || '').toUpperCase();
  const parts = [];
  if (type) parts.push(type.endsWith('.') ? type : `${type}.`);
  parts.push((loc + area).trim());
  if (time) parts.push(time);
  return parts.filter(Boolean).join(' ');
}

export function printSides({ scriptText, scenes, layout = 'half-letter', options = {} }) {
  if (!scriptText || !Array.isArray(scenes) || scenes.length === 0) return;
  const pageWidth = 5.5;
  const pageHeight = 8.5;
  const meas = scaledMeasurements(pageWidth, pageHeight);
  // Override margins for sides: equal top/bottom, loosen left/right
  meas.LEFT_MARGIN = 1.0;
  meas.RIGHT_MARGIN = 0.75;
  meas.TOP_MARGIN = 0.9;
  meas.BOTTOM_MARGIN = 0.9;
  // Recompute LINES_PER_PAGE based on effective content height (exclude top/bottom padding)
  const baseContentHeight = BASE.PAGE_HEIGHT - BASE.TOP_MARGIN - BASE.BOTTOM_MARGIN; // 9in
  const sidesContentHeight = pageHeight - meas.TOP_MARGIN - meas.BOTTOM_MARGIN; // reflects 1.0in bottom
  meas.LINES_PER_PAGE = Math.max(
    1,
    Math.floor(BASE.LINES_PER_PAGE * (sidesContentHeight / baseContentHeight)) - 1 // safety buffer
  );
  const stylesObj = elementStyles(meas);

  // Build a new content string containing only selected scenes (by robust header slice)
  const headers = scenes.map(buildSceneHeader);
  const normalizeSlug = (s) => String(s)
    .toUpperCase()
    .replace(/^\.+/, '')               // trim leading dots
    .replace(/[–—]/g, '-')              // normalize dashes
    .replace(/[^A-Z0-9\- ]+/g, ' ')     // strip punctuation
    .replace(/\s+/g, ' ')               // collapse whitespace
    .trim();
  const headersNorm = headers.map(normalizeSlug);
  const normalized = String(scriptText).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const allLines = normalized.split('\n');
  // Build list of ALL scene header indices in script (to bound sections)
  const isSceneHeaderLine = (raw) => {
    const up = String(raw).trim().toUpperCase().replace(/^\.+/, '');
    return SCENE_PREFIXES.some(pref => up.startsWith(pref));
  };
  const allHeaderIdx = [];
  for (let i = 0; i < allLines.length; i++) {
    if (isSceneHeaderLine(allLines[i])) allHeaderIdx.push(i);
  }
  // Map each scheduled scene to its starting index in the script, preserving schedule order
  const headerIdx = [];
  const matchedScenes = [];
  const usedStarts = new Set();
  for (let s = 0; s < headersNorm.length; s++) {
    const target = headersNorm[s];
    // search only among known scene header lines to avoid false positives
    let foundIdx = -1;
    for (let k = 0; k < allHeaderIdx.length; k++) {
      const idx = allHeaderIdx[k];
      if (usedStarts.has(idx)) continue;
      const normLine = normalizeSlug(allLines[idx]);
      if (normLine.startsWith(target)) { foundIdx = idx; break; }
    }
    if (foundIdx !== -1) {
      headerIdx.push(foundIdx);
      matchedScenes.push(scenes[s]);
      usedStarts.add(foundIdx);
    }
  }
  if (!headerIdx.length) {
    console.warn('SidesPrinter: No matching scene headers found for selected scenes. Aborting sides print to avoid printing entire script.');
    return;
  }
  const collected = [];
  const showNumberAbove = options?.showNumberAbove !== false;
  for (let i = 0; i < headerIdx.length; i++) {
    const start = headerIdx[i];
    // End at the next actual scene header after start
    const nextAllHeader = allHeaderIdx.find(idx => idx > start);
    const end = (typeof nextAllHeader === 'number') ? nextAllHeader : allLines.length;
    const section = allLines.slice(start, end);
    if (section.length > 0) {
      // Attach numeric scene label to slugline and render right-justified later
      const sceneObj = matchedScenes[i];
      const schedNum = sceneObj?.sceneNumber?.standard || sceneObj?.sceneNumber || sceneObj?.number;
      const label = (schedNum !== undefined && schedNum !== null && String(schedNum).length > 0) ? String(schedNum) : String(i + 1);
      section[0] = `${section[0]}|||${label}`;
    }
    collected.push(...section);
    collected.push(''); // blank line between sections
  }
  const selectedContent = collected.join('\n');

  const pages = formatContentToPages(selectedContent, meas);

  // Render page HTML
  const pageHtml = pages.map((page, pi) => {
    const linesHtml = page.map((ln, li) => {
      const style = stylesObj[ln.type] || stylesObj.action;
      const styleStr = Object.entries(style).map(([k,v]) => `${k.replace(/[A-Z]/g, m => '-' + m.toLowerCase())}:${v}`).join(';');
      const text = ln.text || '';
      if (ln.type === 'scene' && text.includes('|||')) {
        const [slug, num] = text.split('|||');
        const left = escapeHtml(slug.trim());
        const right = escapeHtml((num || '').trim());
        return `<div data-line="${li}" style="${styleStr};display:flex;justify-content:space-between;align-items:baseline;">
                  <span>${left || '&nbsp;'}</span>
                  <span style="margin-left:auto;">${right}</span>
                </div>`;
      }
      const safe = escapeHtml(text);
      return `<div data-line="${li}" style="${styleStr}">${safe || '&nbsp;'}</div>`;
    }).join('');
    return `<div class="sides-page">${linesHtml}</div>`;
  });

  // If two-up: wrap every two pages into a sheet
  const contentHtml = (layout === 'two-up')
    ? pageHtml.map((ph, i) => {
        const left = ph;
        const right = pageHtml[i + 1] || '<div class="sides-page"></div>';
        if (i % 2 === 0) {
          return `<div class="sheet"><div class="half left">${left}</div><div class="half right">${right}</div></div>`;
        }
        return '';
      }).join('')
    : pageHtml.join('');

  const pageSize = layout === 'two-up' ? '8.5in 11in' : '5.5in 8.5in';
  const padding = `${meas.TOP_MARGIN}in ${meas.RIGHT_MARGIN}in ${meas.BOTTOM_MARGIN}in ${meas.LEFT_MARGIN}in`;
  const styles = `
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');
      :root { --screenplay-font: 'Courier Prime', Courier, monospace; }
      @page { size: ${pageSize}; margin: 0; }
      @media print {
        html, body { margin:0; padding:0; }
        body { font-family: var(--screenplay-font); }
        .sides-page { width: 5.5in; height: 8.5in; box-sizing: border-box; padding: ${padding}; page-break-after: always; position: relative; overflow: hidden; }
        .sides-page:last-child { page-break-after: auto; }
        .sheet { width: 8.5in; height: 11in; position: relative; page-break-after: always; }
        .half { position: absolute; top: 0; bottom: 0; width: 50%; }
        .half.left { left: 0; padding: 0; }
        .half.right { right: 0; padding: 0; }
        .half .sides-page { width: 5.5in; height: 8.5in; padding: ${padding}; box-sizing: border-box; position: absolute; top: 1.25in; overflow: hidden; }
        /* Simple vertical cut mark */
        .sheet::before { content: ''; position: absolute; top: 0.5in; left: 50%; width: 1px; height: 10in; background: #000; opacity: 0.2; transform: translateX(-0.5px); }
      }
    </style>
  `;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">${styles}</head><body>${contentHtml}</body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  const doc = (iframe.contentWindow || iframe.contentDocument).document || iframe.contentDocument;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try { (iframe.contentWindow || iframe.contentDocument).focus(); } catch (e) {}
    try { (iframe.contentWindow || iframe.contentDocument).print(); } catch (e) { window.print(); }
    setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 500);
  }, 350);
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
